import './styles.scss';

import { Button, Col, Row } from 'react-bootstrap';
import { CURRENT_DATE, DAYS_OF_THE_WEEK } from '@/constants';
import { ConnectionDetails, QueryInputType, queryInput } from '@/utils/custom';
import { CreateUserAvailabilityInput, SortDirectionEnum, User } from 'models/gen/graphql';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { UserWithAvailabilitySearch, useSearchUsersWithAvailability } from '@/api/services/users/searchUsers';
import VirtualTable, { DynamicCell, VirtualTableRow, useVirtualTable } from '@/components/VirtualTable';
import { getClasses, parsePhoneNumber } from '@/utils/strings';

import { AvailabilityAction } from '@/components/EditAvailabilityPopover';
import AvailabilityDisplay from '@/components/AvailabilityDisplay';
import AvailabilityFilters from './AvailabilityFilters';
import { Datetime } from '@/utils/dates';
import ImageDisplay from '@/components/ImageDisplay';
import PageInfo from '@/components/PageInfo';
import { Validation } from '@/utils/validations';
import { stringify } from '@/utils';

type AvailabilityTableState = {
  search: string;
  sortDate: string;
  dayOfWeek: number;
  from: string;
  to: string;
};

const initAvailabilityTableState: AvailabilityTableState = {
  search: '',
  sortDate: new Datetime(CURRENT_DATE).dateInput,
  dayOfWeek: new Datetime(CURRENT_DATE).day,
  from: new Datetime(CURRENT_DATE.clone().startOf('week')).setTime('00:00:00').toString(),
  to: new Datetime(CURRENT_DATE.clone().endOf('week')).setTime('23:59:59').toString(),
};

const AvailabilityTable = (): JSX.Element => {
  const [state, setState] = useState({ ...initAvailabilityTableState });
  const { sortDate, from, search } = state;
  const appliedQuery = useRef(undefined);
  const startTimeSortDirection = useRef(SortDirectionEnum.Asc);
  const [{ data, loading }, { refetch, setData }] = useSearchUsersWithAvailability();
  const { rows = [] } = data || {};
  const { filteredRows } = useVirtualTable(setState, { rows, search });
  const previousRows = useRef([]);
  const onSubmit = useCallback(
    async (filters) => {
      const query = {
        user: {
          roleId: Validation.isValidUUID(filters?.roleId) ? queryInput(filters?.roleId) : null,
          firstName: queryInput(
            [],
            QueryInputType.DEFAULT,
            startTimeSortDirection.current === SortDirectionEnum.Asc ? SortDirectionEnum.Asc : SortDirectionEnum.Desc,
            0
          ),
        },
        availability: {
          startTime: queryInput(
            [],
            QueryInputType.DEFAULT,
            startTimeSortDirection.current === SortDirectionEnum.Asc ? SortDirectionEnum.Asc : SortDirectionEnum.Desc,
            0
          ),
          approved: filters?.viewRequests ? queryInput([], QueryInputType.ISNULL) : null,
        },
      };
      const airportCodes = filters?.city?.length ? filters?.city : null;

      setState(
        (current: AvailabilityTableState): AvailabilityTableState => ({
          ...current,
          sortDate: filters?.sortDate || new Datetime(filters?.from).add(current?.dayOfWeek, 'days').dateInput,
          from: filters?.from,
          to: filters?.to,
        })
      );

      const search: UserWithAvailabilitySearch = {
        query: [query?.user],
        availabilityQuery: [query?.availability],
        airportCodes,
        sortDate: filters?.sortDate || sortDate,
        startDatetime: filters?.from,
        endDatetime: filters?.to,
      };

      await refetch(search);

      appliedQuery.current = {
        filters,
        query,
        airportCodes,
        sortDate: filters?.sortDate || sortDate,
        from: filters?.from,
        to: filters?.to,
      };
    },
    [sortDate, refetch]
  );

  const handleSetUserData = (actions: Partial<AvailabilityAction> | Record<'add', CreateUserAvailabilityInput>): void => {
    const [action, value] = Object.entries(actions || [])[0];
    if (!action || !value) return;

    setData((current: ConnectionDetails<User>) => {
      if (action === 'revert' && value === true) return { ...current, rows: previousRows.current || current?.rows };

      const updatedRows = stringify.parse(current?.rows || []);
      const user: User = updatedRows?.find((user: User) => user?.id === (action === 'add' ? value?.[0] : value)?.userId);
      if (!user) return current;
      if (!user.availability) user.availability = [];

      previousRows.current = current?.rows;
      const availabilityIndex = user.availability.findIndex((availability) => availability.id === value?.id);

      switch (action) {
        case 'add':
          user.availability = user.availability.concat(value);
          break;

        case 'update':
        case 'updateSeries':
          if (availabilityIndex === -1) break;
          Object.assign(user.availability[availabilityIndex], value);
          break;

        case 'approve':
        case 'deny':
        case 'delete':
        case 'deleteSeries':
          if (availabilityIndex === -1) break;
          user.availability.splice(availabilityIndex, 1);
          break;
      }
      return { ...current, rows: updatedRows };
    });
  };

  const onSortDayOfWeek = useCallback(
    (i: number): any =>
      (): void => {
        const date = new Datetime(appliedQuery?.current?.from).startOf('week').add(i, 'days');
        const output = {
          ...state,
        };
        startTimeSortDirection.current =
          startTimeSortDirection.current === SortDirectionEnum.Asc ? SortDirectionEnum.Desc : SortDirectionEnum.Asc;
        if (date.dateInput !== sortDate) {
          output.sortDate = date.dateInput;
          output.dayOfWeek = i;
          startTimeSortDirection.current = SortDirectionEnum.Asc;
        }
        setState(output);
        onSubmit({ ...appliedQuery?.current?.filters, sortDate: output?.sortDate });
      },
    [state, startTimeSortDirection, sortDate, onSubmit]
  );
  const onRefetch = useCallback(async (): Promise<any> => await refetch(), [refetch]);

  const handleTableSearch = ({ search }: any): void =>
    setState((current: AvailabilityTableState): AvailabilityTableState => ({ ...current, search }));

  const headers = DAYS_OF_THE_WEEK.reduce((acc: {}, cur: string, i: number): Record<string, React.JSX.Element> => {
    acc[cur] = (
      <div className="AvailabilityTable-Header">
        <Button
          className="d-flex justify-content-center align-items-center gap-4 w-100 h-100"
          variant="icon"
          onClick={() => onSortDayOfWeek(i)()}
        >
          <div className="d-flex flex-column align-items-start">
            <strong>{new Datetime(appliedQuery?.current?.from).startOf('week').add(i, 'days').format('dddd')}</strong>
            <strong>{new Datetime(appliedQuery?.current?.from).startOf('week').add(i, 'days').format('MMMM D')}</strong>
          </div>
          <div className="d-flex flex-column justify-content-center">
            {i !== new Datetime(sortDate).day ? (
              <i className="fa fa-sort opacity-25" />
            ) : (
              <i className={`fa fa-caret-${startTimeSortDirection.current === SortDirectionEnum.Desc ? 'down' : 'up'}`} />
            )}
          </div>
        </Button>
      </div>
    );
    return acc;
  }, {});

  const renderCell = useCallback(
    (i: number): (({ data }) => JSX.Element) =>
      function renderCell({ data }: { data: User }): JSX.Element {
        return (
          <div className="AvailabilityTable-Cell">
            <AvailabilityDisplay user={data} date={from} index={i} setUserData={handleSetUserData} />
          </div>
        );
      },
    [from, onRefetch]
  );

  const renderRow = useCallback(
    ({ index, data: { _type, ...data } = {}, context = {} }: { index: any; data: any; context: any }): JSX.Element => (
      <VirtualTableRow
        context={{
          ...context,
          rowType: _type,
          data,
          index,
          className: getClasses('VirtualTable-Row', 'd-flex', context?.className),
        }}
      >
        <DynamicCell
          selector="user"
          placeholder="--"
          className="text-center alternate"
          width="calc(200% / 8)"
          render={({ data: user }: { data: User }): ReactNode => (
            <Row className="m-0 flex-nowrap">
              <Col className="profile-image d-none d-lg-flex">
                <ImageDisplay className="{width:4.5rem!;height:4.5rem!;}" src={user?.avatar} circle />
              </Col>
              <Col xs={12} md={8} className="profile-info flex-grow-1 ps-1 pe-0">
                <p className="m-0 text-center text-xl-start">
                  <strong className="d-block">
                    {user?.firstName || ''} {user?.lastName || ''}
                  </strong>
                  <small className="d-block">{parsePhoneNumber(user?.phone || '', ' ') || '-'}</small>
                  <small className="d-block employee-id">{user?.employeeId ? `#${user?.employeeId}` : '-'}</small>
                </p>
              </Col>
            </Row>
          )}
          options={{ showTooltip: false }}
        />
        {DAYS_OF_THE_WEEK.map(
          (dayOfWeek: string, i: number): JSX.Element => (
            <DynamicCell
              selector={dayOfWeek}
              placeholder="--"
              className={getClasses('text-center', i % 2 > 0 ? 'alternate' : '')}
              width="calc(100% / 8)"
              render={renderCell(i)}
              options={{ showTooltip: false }}
              key={dayOfWeek}
            />
          )
        )}
      </VirtualTableRow>
    ),
    [renderCell]
  );

  return (
    <div className="d-flex flex-column">
      <AvailabilityFilters
        dayOfWeek={state?.dayOfWeek}
        onSubmit={onSubmit}
        onReset={(): void => setState({ ...initAvailabilityTableState, search: '' })}
        onFilter={handleTableSearch}
      />
      <PageInfo>Total Users: {filteredRows?.length}</PageInfo>
      <VirtualTable
        name="availabilityTable"
        data={filteredRows}
        loading={loading}
        className="AvailabilityTable"
        header={{
          user: 'User',
          ...headers,
        }}
        rowRenderer={renderRow}
      />
    </div>
  );
};

export default AvailabilityTable;
